import { useCallback, useEffect, useRef } from 'react';

const DEFAULT_TIMEOUT_MS = 15 * 60 * 1000;
const DEFAULT_WARNING_MS = 2 * 60 * 1000;
const CHECK_INTERVAL_MS = 10_000;
/** Limit how often we write lastActivity from high-frequency events (no state updates). */
const ACTIVITY_THROTTLE_MS = 500;

function clampPositiveMs(n: unknown, fallback: number): number {
  if (typeof n !== 'number' || !Number.isFinite(n) || n <= 0) return fallback;
  return n;
}

export type UseSessionTimeoutOptions = {
  timeoutMs?: number;
  warningMs?: number;
  onWarn: () => void;
  onLogout: () => void;
  /** Called when user activity resets the idle clock while a warning may be showing. */
  onUserActivity?: () => void;
  enabled?: boolean;
};

/**
 * Tracks inactivity via refs (no re-renders on activity). Pauses checks while tab is hidden.
 * Warns when `timeoutMs - warningMs` idle; logs out when `timeoutMs` idle.
 */
export function useSessionTimeout(options: UseSessionTimeoutOptions): { resetTimer: () => void } {
  const {
    timeoutMs: timeoutMsProp,
    warningMs: warningMsProp,
    onWarn,
    onLogout,
    onUserActivity,
    enabled = true,
  } = options;

  const lastActivityRef = useRef(Date.now());
  const warningIssuedRef = useRef(false);

  const callbacksRef = useRef({ onWarn, onLogout, onUserActivity });
  callbacksRef.current = { onWarn, onLogout, onUserActivity };

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    warningIssuedRef.current = false;
  }, []);

  useEffect(() => {
    if (!enabled) {
      warningIssuedRef.current = false;
      return;
    }

    const timeoutMs = clampPositiveMs(timeoutMsProp, DEFAULT_TIMEOUT_MS);
    const warningMsRaw = clampPositiveMs(warningMsProp, DEFAULT_WARNING_MS);
    const warningMs = Math.min(warningMsRaw, Math.max(timeoutMs - 1000, 1000));
    const warnIdleMs = timeoutMs - warningMs;

    let lastThrottleWrite = 0;

    function recordActivity() {
      if (document.hidden) return;
      const now = Date.now();
      if (now - lastThrottleWrite < ACTIVITY_THROTTLE_MS) return;
      lastThrottleWrite = now;
      lastActivityRef.current = now;
      if (warningIssuedRef.current) {
        warningIssuedRef.current = false;
        callbacksRef.current.onUserActivity?.();
      }
    }

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart'] as const;
    for (const ev of events) {
      window.addEventListener(ev, recordActivity, { passive: true });
    }
    document.addEventListener('scroll', recordActivity, { passive: true, capture: true });

    function runCheck() {
      const { onWarn: ow, onLogout: ol } = callbacksRef.current;
      if (document.hidden) return;
      const now = Date.now();
      const idle = now - lastActivityRef.current;
      if (idle >= timeoutMs) {
        ol();
        return;
      }

      if (idle >= warnIdleMs && !warningIssuedRef.current) {
        warningIssuedRef.current = true;
        ow();
      }
    }

    const intervalId = window.setInterval(runCheck, CHECK_INTERVAL_MS);

    function onVisibilityChange() {
      if (!document.hidden) runCheck();
    }
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      document.removeEventListener('scroll', recordActivity, true);
      for (const ev of events) {
        window.removeEventListener(ev, recordActivity);
      }
    };
  }, [enabled, timeoutMsProp, warningMsProp]);

  return { resetTimer };
}
